import {
  ClipboardDocumentIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import {
  AssessmentPart,
  ClaimAssessmentStatus,
  ExternalRepairInvoiceStatus,
} from '@rabbit/bizproc/core';
import { useManageFaults } from '@rabbit/bizproc/react';
import { AppContext } from '@rabbit/app-context';
import {
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
} from '@rabbit/data/types';
import {
  Button,
  CardWrapperWithHeader,
  LabelledItemList,
  LabelledItemShape,
  Modal,
  Input,
} from '@rabbit/elements/shared-components';
import UpdateClaimAssessmentModal from '@rabbit/sage/components/organisms/case-flow-modals/UpdateClaimAssessmentModal/UpdateClaimAssessmentModal';
import { CaseflowContext } from '@rabbit/sage/context/CaseflowContext';
import { stripTags } from '@rabbit/sage/utils/helpers';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { partsAssessmentData } from '@rabbit/sage/components/molecules/claims/ClaimCostsSection/ClaimCostsSectionTableColumns.tsx';
import { CreateTable } from '@rabbit/sage/utils/CreateTable.tsx';
import { useGetMySagePersonas } from '@rabbit/data/portal';
import { toast } from 'react-toastify';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { SageFileUploader } from '@rabbit/sage/components/organisms/upload-wrapper/SageFileUploader.tsx';
import {
  DocTypeShapeTypes,
  UploadedFileCategories,
} from '@rabbit/elements/shared-types';

interface RejectFormValuesShape {
  rejectionReason: string;
}

const rejectValidationSchema = Yup.object().shape({
  rejectionReason: Yup.string().required('Rejection reason is required'),
});

export default function ClaimAssessmentSection({
  repairAssessment = false,
}: {
  repairAssessment?: boolean;
}) {
  const {
    holdingData,
    caseFacts,
    installerCaseFacts,
    repairerCaseFacts,
    warrantorCasefacts,
    operatingPersona,
    alterCaseFacts,
  } = useContext(CaseflowContext) || {};
  const { t } = useTranslation();
  const { config } = useContext(AppContext);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { installerPersona, repairerPersona, warrantorPersona } =
    useGetMySagePersonas();
  const { faultList } = useManageFaults();
  const faultOptions = !faultList?.data
    ? []
    : faultList.data.map((fault) => ({
        id: fault.docid,
        value: fault.docid,
        label: fault.title,
        name: fault.title,
      }));

  const [assessmentModal, setAssessmentModal] = useState(false);

  const renderAssessmentAttachment = (attachment: any) => {
    let totalFiles = attachment?.length ?? 0;

    return (
      <div
        className={
          'flex cursor-pointer items-center gap-2 text-sm ' +
          (totalFiles === 0 ? 'text-red-500' : 'text-black')
        }
        onClick={() => setAssessmentModal(true)}
      >
        {totalFiles} {t('general.filesUploaded')}
        <div>
          <ArrowTopRightOnSquareIcon
            className={
              'h-4 w-4' + (totalFiles === 0 ? 'text-red-500' : 'text-black')
            }
          />
        </div>
      </div>
    );
  };

  // Determine which case facts to use based on persona existence and repairAssessment flag
  const currentCaseFacts = repairAssessment
    ? installerPersona?.personaId || config.HOLDINGS.INSTALLATION_REQUIRED
      ? installerCaseFacts
      : repairerCaseFacts
    : warrantorCasefacts;

  const items: any[] = [];
  currentCaseFacts?.repair_work_registry?.forEach((item) => {
    items.push(...item.parts_used_for_repair);
  });
  const parts = currentCaseFacts?.assessment_parts?.map((part) => part.name);
  const totalParts = currentCaseFacts?.assessment_parts;

  const record: LabelledItemShape[] = [
    ...(config.CLAIMS.CLAIMS_FLOW.PARTS_ASSESSMENT
      ? []
      : [
          {
            label: t('general.parts'),
            value: parts?.join(', ') || '-',
          },
        ]),
    {
      label: t('general.faults'),
      value:
        currentCaseFacts?.holding_faults
          ?.map((id) => faultOptions.find((f) => f.id === id)?.label)
          .join(', ') || '-',
    },
    ...(repairAssessment
      ? [
          {
            label: t('general.assessment'),
            value: currentCaseFacts?.preliminary_assessment || '-',
          },
          ...(caseFacts?.assessment_status === ClaimAssessmentStatus.REJECTED
            ? [
                {
                  label: t('general.rejectionReason'),
                  value: caseFacts?.assessment_rejection_reason || '-',
                },
              ]
            : []),
        ]
      : []),

    ...(!repairAssessment
      ? [
          {
            label: t('general.finalAssessment'),
            value: currentCaseFacts?.final_assessment || '-',
          },
          {
            label: t('general.outcome'),
            value: currentCaseFacts?.claim_outcome || '-',
          },
          {
            label: t('general.cause'),
            value: currentCaseFacts?.claim_cause || '-',
          },
        ]
      : []),
    {
      label: t('general.additionalDocuments'),
      value:
        renderAssessmentAttachment(currentCaseFacts?.assessment_attachment) ||
        '',
    },

    {
      label: t('general.additionalComment'),
      value: stripTags(currentCaseFacts?.assessment_comment || '') || '-',
    },
  ];

  const isRepairer = operatingPersona?.startsWith(
    PersonaTypeSingleLetter.Repairer + PersonaIdTypeSplitter
  );
  const isInstaller = operatingPersona?.startsWith(
    PersonaTypeSingleLetter.Installer + PersonaIdTypeSplitter
  );

  const isWarrantor = operatingPersona?.startsWith(
    PersonaTypeSingleLetter.Warrantor + PersonaIdTypeSplitter
  );

  const handleApproveAssessment = async () => {
    if (!alterCaseFacts) return;

    try {
      setIsSubmitting(true);
      await alterCaseFacts({
        assessment_status: ClaimAssessmentStatus.APPROVED,
        final_assessment:
          installerCaseFacts?.preliminary_assessment ??
          repairerCaseFacts?.preliminary_assessment ??
          caseFacts?.final_assessment,
      });
      toast.success(t('general.assessmentApprovedSuccessfully'));
    } catch (err) {
      toast.error(t('message.failedToApproveAssessment'));
      console.log(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectAssessment = async (values: RejectFormValuesShape) => {
    if (!alterCaseFacts || !values.rejectionReason) return;

    try {
      setIsSubmitting(true);
      await alterCaseFacts({
        assessment_status: ClaimAssessmentStatus.REJECTED,
        assessment_rejection_reason: values.rejectionReason,
      });
      toast.success(t('general.assessmentRejectedSuccessfully'));
      setShowRejectModal(false);
    } catch (err) {
      toast.error(t('message.failedToRejectAssessment'));
      console.log(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  let hideAssessment = false;
  if (isRepairer) {
    if (!caseFacts?.external_repair_invoices) return;
    const invoiceNotApproved =
      (
        caseFacts?.external_repair_invoices
          ?.map((invoice) => {
            return invoice?.status !== ExternalRepairInvoiceStatus.APPROVED;
          })
          .filter((i) => i) || []
      )?.length > 0;

    if (invoiceNotApproved) {
      hideAssessment = true;
    } else if (caseFacts?.final_assessment === 'Not covered') {
      hideAssessment = true;
    }
  }

  const renderPartsTable = (
    label: string,
    columns: any,
    data: AssessmentPart[]
  ) => {
    const PartsTable = CreateTable<AssessmentPart>(data || [], columns);
    return (
      <div className="mt-5" key={label} style={{ width: '100%' }}>
        <CardWrapperWithHeader
          title={label}
          canCollapse={true}
          collapsedByDefault={true}
          smaller={true}
          noPadding={true}
        >
          <div className="max-w-[757px] border-gray-100">
            <PartsTable
              enablePagination={data.length > 10}
              initialState={{
                showGlobalFilter: true,
              }}
              muiSearchTextFieldProps={{
                sx: {
                  display: 'none',
                },
              }}
              muiTopToolbarProps={{
                sx: {
                  display: 'none',
                },
              }}
              muiTableHeadCellProps={{
                className: 'relative bg-gray-200 uppercase font-light',
              }}
              muiTableBodyCellProps={{
                className: 'px-4 py-0',
              }}
            />
          </div>
        </CardWrapperWithHeader>
      </div>
    );
  };
  return (
    <CardWrapperWithHeader
      title={
        repairAssessment && (isInstaller || isRepairer)
          ? `${t('general.claimAssessment')}`
          : repairAssessment && !(isInstaller || isRepairer)
          ? config?.HOLDINGS.INSTALLATION_REQUIRED
            ? t('claim.installerAssessment')
            : t('claim.repairAssessment')
          : t('general.claimAssessment')
      }
      headerRight={
        !hideAssessment && (
          <div className="flex items-center gap-2 text-2xl">
            {repairAssessment &&
              caseFacts?.assessment_status &&
              caseFacts?.assessment_status !==
                ClaimAssessmentStatus.PENDING && (
                <div
                  className={`flex items-center gap-1 text-sm font-medium ${
                    caseFacts?.assessment_status ===
                    ClaimAssessmentStatus.APPROVED
                      ? 'text-green-600'
                      : caseFacts?.assessment_status ===
                        ClaimAssessmentStatus.REJECTED
                      ? 'text-red-600'
                      : 'text-gray-600'
                  }`}
                >
                  {caseFacts?.assessment_status ===
                    ClaimAssessmentStatus.APPROVED && (
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                  )}
                  {caseFacts?.assessment_status ===
                    ClaimAssessmentStatus.REJECTED && (
                    <XCircleIcon className="h-5 w-5 text-red-600" />
                  )}
                  <span className="font-nunito text-lg">
                    {caseFacts?.assessment_status}
                  </span>
                </div>
              )}

            {repairAssessment && isWarrantor && (
              <>
                {caseFacts?.assessment_status ===
                  ClaimAssessmentStatus.PENDING && (
                  <Button
                    kind="primary"
                    size="sm"
                    onClick={handleApproveAssessment}
                    loading={isSubmitting}
                    icon={<CheckCircleIcon />}
                    children={t('general.approveAssessment')}
                  />
                )}
                {caseFacts?.assessment_status ===
                  ClaimAssessmentStatus.PENDING && (
                  <Button
                    kind="outline_red"
                    size="sm"
                    onClick={() => setShowRejectModal(true)}
                    loading={isSubmitting}
                    icon={<XCircleIcon />}
                    children={t('general.rejectAssessment')}
                  />
                )}
              </>
            )}
            {((repairAssessment &&
              (isRepairer || isInstaller) &&
              !(
                caseFacts?.assessment_status === ClaimAssessmentStatus.APPROVED
              )) ||
              (!repairAssessment && !(isRepairer || isInstaller))) && (
              <Button
                kind="primary"
                size="sm"
                onClick={() => setShowUpdateModal(true)}
                icon={<ClipboardDocumentIcon />}
                children={t('general.updateAssessment')}
              />
            )}
          </div>
        )
      }
    >
      <LabelledItemList
        items={record}
        itemProps={{
          labelClasses: 'font-nunito mb-[10px] text-sm text-gray-500',
          valueClasses: 'font-nunito font-medium',
        }}
      />
      {config.CLAIMS.CLAIMS_FLOW.PARTS_ASSESSMENT &&
        totalParts &&
        totalParts.length > 0 &&
        renderPartsTable(
          `${t('general.defectiveParts')}`,
          partsAssessmentData(t, faultOptions),
          totalParts
        )}
      {showUpdateModal && (
        <UpdateClaimAssessmentModal onClose={() => setShowUpdateModal(false)} />
      )}
      {showRejectModal && (
        <Modal
          settings={{
            title: t('general.rejectAssessment'),
            headerBackground: true,
            handleClose: () => setShowRejectModal(false),
          }}
          kind="generic"
          isLoading={isSubmitting}
          className="min-w-[500px]"
        >
          <div className="mt-5 flex w-full flex-col gap-3 px-4">
            <Formik
              initialValues={{ rejectionReason: '' }}
              validationSchema={rejectValidationSchema}
              onSubmit={handleRejectAssessment}
            >
              {({ isValid, dirty }) => (
                <Form>
                  <Input
                    type="textarea"
                    label={t('general.rejectionReason')}
                    name="rejectionReason"
                    settings={{
                      placeholder: t('general.pleaseProvideARejectionReason'),
                    }}
                  />
                  <div className="mt-8 flex gap-8">
                    <Button
                      kind="primary"
                      type="submit"
                      loading={isSubmitting}
                      disabled={!isValid || !dirty}
                    >
                      {t('general.rejectAssessment')}
                    </Button>
                    <Button
                      kind="outline_red"
                      type="button"
                      onClick={() => setShowRejectModal(false)}
                    >
                      {t('general.cancel')}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </Modal>
      )}
      {assessmentModal && (
        <Modal
          kind="generic"
          settings={{
            title: t('general.assessmentAttachment'),
            handleClose: () => setAssessmentModal(false),
          }}
          className="m-auto w-[724px] rounded-md border bg-white"
        >
          <div className="px-5">
            <SageFileUploader
              label={t('general.assessmentAttachment')}
              identifiers={{
                category: UploadedFileCategories.SerialNumberProof,
                docType: {
                  docid: caseFacts?.consumer_holding || '',
                  type: DocTypeShapeTypes.Case,
                },
                personaId: installerPersona
                  ? installerPersona?.personaId
                  : repairerPersona
                  ? repairerPersona?.personaId
                  : warrantorPersona?.personaId || '',
              }}
              alterCaseFacts={alterCaseFacts}
              accepts={['image/*', '.pdf']}
              shouldAutoUpdateDocs={false}
              maxFiles={1}
              allowDelete={false}
              currentFiles={currentCaseFacts?.assessment_attachment ?? []}
            />
          </div>
          <div className="mt-4 flex w-full gap-2 px-4">
            <Button kind="red" onClick={() => setAssessmentModal(false)}>
              {t('Close')}
            </Button>
          </div>
        </Modal>
      )}
    </CardWrapperWithHeader>
  );
}
